import { ComponentProps, useState, createContext, useContext, ReactNode } from 'react';
import { Collapsible as RCollapsible } from 'radix-ui';
import { motion, AnimatePresence } from 'motion/react';
import { MaterialSymbol, withStyles } from 'vopay';
import styles from './CollapsibleList.module.scss';

// Moved from client-dashboard's SubAccounts permissions drawer (ID-13343). The Users → Permissions screens
// in both portals keep their own copies.

const cn = withStyles(styles);

// A whole section appears/disappears when a dependency toggle adds it to or prunes it from `visibleSections`
// (ID-13344). The `AnimatePresence` here plus a `motion` item root grow/shrink it in so the sections below
// slide along instead of jumping. The height timing matches radix's own expand/collapse keyframes (see
// CollapsibleList.module.scss) so this animation doesn't fight radix when the user later opens the section.
const ITEM_HEIGHT_EASE = [0.7, 0, 0, 1] as const;
const ITEM_HEIGHT_DURATION = 0.4;

type ListProps = {
  children: ReactNode;
  className?: string;
};

export default function List({ children, className }: ListProps) {
  return (
    <div className={cn('collapsibleListContainer', className)}>
      {/* initial={false} skips the enter animation on first mount (drawer open); later additions animate. */}
      <AnimatePresence initial={false}>{children}</AnimatePresence>
    </div>
  );
}

type ItemContext = {
  open: boolean;
};

const ItemContext = createContext<ItemContext>({
  open: false,
});

type ItemProps = ComponentProps<typeof RCollapsible.Root>;

/**
 * `open` is not fully controlled: whenever it changes the item snaps to it, and the user can still toggle
 * the item in between (search forces every section open, clearing it collapses them again).
 */
function Item({ className, children, style, open: openProp, defaultOpen, onOpenChange, ...props }: ItemProps) {
  const [open, setOpen] = useState<boolean>(openProp ?? defaultOpen ?? false);
  const [lastOpenProp, setLastOpenProp] = useState(openProp);

  // Adjust state while rendering when the prop changes (React's documented alternative to a syncing effect).
  if (openProp !== lastOpenProp) {
    setLastOpenProp(openProp);
    if (openProp !== undefined) setOpen(openProp);
  }

  return (
    <RCollapsible.Root
      {...props}
      open={open}
      onOpenChange={(val) => {
        setOpen(val);
        onOpenChange?.(val);
      }}
      asChild
    >
      {/* `asChild` so the item's own mount/unmount is the animated element — the section grows/shrinks in.
          `style` is forwarded (callers pass the depth CSS var) since `asChild` hands radix's props to us. */}
      <motion.div
        className={cn('collapsibleListItem', className)}
        style={style}
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: ITEM_HEIGHT_DURATION, ease: ITEM_HEIGHT_EASE }}
      >
        <ItemContext.Provider value={{ open }}>{children}</ItemContext.Provider>
      </motion.div>
    </RCollapsible.Root>
  );
}

List.Item = Item;

type TriggerProps = Omit<ComponentProps<typeof RCollapsible.Trigger>, 'children'> & {
  children: ReactNode | ((args: { open: boolean }) => ReactNode);
  'data-testid'?: string;
  className?: string;
};

function Trigger({ children, className, 'data-testid': dataTestID, ...props }: TriggerProps) {
  const { open } = useContext(ItemContext);

  return (
    <RCollapsible.Trigger
      data-testid={dataTestID || 'collapsibleListTrigger'}
      {...props}
      className={cn('collapsibleTrigger', className)}
      asChild
    >
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;

          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.currentTarget.click();
          }
        }}
      >
        <div className={cn('chevron', open && 'open')}>
          <MaterialSymbol icon="keyboard_arrow_down" size="24px" />
        </div>

        {typeof children === 'function' ? children({ open }) : children}
      </div>
    </RCollapsible.Trigger>
  );
}

List.Trigger = Trigger;

type ContentProps = ComponentProps<typeof RCollapsible.Content> & {
  className?: string;
};

function Content({ className, children, ...props }: ContentProps) {
  return (
    <RCollapsible.Content {...props} className={cn('collapsibleContent', className)}>
      <div className={cn('collapsibleContentInner')}>{children}</div>
    </RCollapsible.Content>
  );
}

List.Content = Content;
